import React, { useEffect } from "react";
import { X } from "lucide-react";

export const Modal = ({ open, onClose, title, eyebrow, children, footer, width = 560, testid }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && !e.defaultPrevented && onClose?.();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center px-3 py-5 md:px-4 md:py-10" data-testid={testid}>
      <div className="absolute inset-0 bg-slate-950/45 backdrop-blur-sm" onClick={onClose} />
      <div
        className="relative w-full rounded-3xl bg-white border border-slate-200 cf-shadow-xl overflow-hidden cf-fade-up flex flex-col cf-modal-viewport-fit"
        style={{
          maxWidth: width,
          maxHeight: "min(calc(100vh - 40px), calc(100dvh - 40px))",
          marginTop: "env(safe-area-inset-top, 0px)",
          marginBottom: "env(safe-area-inset-bottom, 0px)",
        }}
      >
        <div className="flex items-start justify-between gap-3 px-5 md:px-7 py-4 md:py-5 border-b border-slate-100 bg-gradient-to-r from-indigo-50/70 via-white to-white">
          <div>
            {eyebrow && <p className="cf-eyebrow cf-eyebrow-emerald mb-1.5">{eyebrow}</p>}
            <h2 className="cf-h-section">{title}</h2>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-colors" aria-label="Close" data-testid="modal-close">
            <X size={15} />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto cf-scroll px-5 md:px-7 py-5 md:py-6">{children}</div>
        {footer && <div className="shrink-0 px-5 md:px-7 py-3 md:py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-2">{footer}</div>}
      </div>
    </div>
  );
};

export const Drawer = ({ open, onClose, title, eyebrow, children, footer, width = 520, testid, headerExtra, aside, zClass = "z-[90]" }) => {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && !e.defaultPrevented && onClose?.();
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  return (
    <div
      className={`fixed inset-0 ${zClass} ${open ? "pointer-events-auto" : "pointer-events-none"}`}
      data-testid={testid}
      inert={!open}
      aria-hidden={!open || undefined}
      style={{ visibility: open ? "visible" : "hidden", transition: open ? "visibility 0s" : "visibility 0s linear 320ms" }}
    >
      <div
        className={`absolute inset-0 bg-slate-950/45 backdrop-blur-sm transition-opacity duration-300 ${open ? "opacity-100" : "opacity-0"}`}
        onClick={onClose}
      />
      <div
        className="absolute right-0 top-0 bottom-0 bg-white border-l border-slate-200 cf-shadow-xl flex"
        style={{
          width,
          maxWidth: "96vw",
          transform: open ? "translateX(0)" : "translateX(100%)",
          transition: "transform 360ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        {/* Main column */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex items-start justify-between gap-3 px-5 md:px-7 py-4 md:py-5 border-b border-slate-100 bg-gradient-to-r from-indigo-50/70 via-white to-white">
            <div className="min-w-0">
              {eyebrow && <p className="cf-eyebrow cf-eyebrow-emerald mb-1.5">{eyebrow}</p>}
              <h2 className="cf-h-section truncate">{title}</h2>
            </div>
            {headerExtra}
            <button onClick={onClose} className="w-9 h-9 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-colors shrink-0" aria-label="Close" data-testid="drawer-close">
              <X size={15} />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto cf-scroll px-5 md:px-7 py-5 md:py-6">{children}</div>
          {footer && <div className="shrink-0 px-5 md:px-7 py-3 md:py-4 border-t border-slate-100 bg-slate-50/70 backdrop-blur-sm flex items-center gap-2" style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom, 0px))" }}>{footer}</div>}
        </div>
        {/* Optional aside (right-side context panel) */}
        {aside && (
          <aside className="hidden xl:flex flex-col w-[320px] shrink-0 border-l border-slate-100 bg-gradient-to-b from-indigo-50/40 via-white to-white overflow-y-auto cf-scroll">
            {aside}
          </aside>
        )}
      </div>
    </div>
  );
};

// Static span lookup so Tailwind JIT picks them up at build-time.
// Use mobile-first: stack on mobile, half on small, requested width on md+.
const SPAN_CLASS = {
  3:  "col-span-12 sm:col-span-6 md:col-span-3",
  4:  "col-span-12 sm:col-span-6 md:col-span-4",
  6:  "col-span-12 sm:col-span-6",
  8:  "col-span-12 md:col-span-8",
  9:  "col-span-12 md:col-span-9",
  12: "col-span-12",
};

export const Field = ({ label, hint, error, children, span = 12 }) => (
  <label className={`flex flex-col gap-1.5 ${SPAN_CLASS[span] || SPAN_CLASS[12]}`}>
    <span className="text-[11px] uppercase tracking-[0.16em] text-slate-500 font-semibold">{label}</span>
    {children}
    {error ? <span className="text-[11px] text-rose-600 font-medium">{error}</span> : hint ? <span className="text-[11px] text-slate-400">{hint}</span> : null}
  </label>
);

export const inputClass =
  "h-10 px-3 rounded-lg border border-slate-200 bg-white text-[13px] text-slate-800 placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none transition-all";

export const textareaClass =
  "px-3 py-2.5 rounded-lg border border-slate-200 bg-white text-[13px] text-slate-800 placeholder:text-slate-400 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 focus:outline-none transition-all resize-none";
